import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type SettingsSection =
  | 'account'
  | 'billing'
  | 'appearance'
  | 'notifications'
  | 'agents'
  | 'contacts'
  | 'invites'
  | 'spaces'
  | 'about'
  | 'sign-out';

interface SettingsDrawerState {
  isOpen: boolean;
  activeSection: SettingsSection;
  open: (section?: SettingsSection) => void;
  close: () => void;
  setActiveSection: (section: SettingsSection) => void;
}

const SettingsDrawerContext = createContext<SettingsDrawerState | null>(null);

// T-0221 (packet 007, D21) — `/settings` is a REAL route now: a left-nav destination
// must be linkable + refresh-safe. The drawer's open state is BOUND to the URL, so a
// direct load / a refresh of `/settings` opens the drawer, and the rail gear + mobile
// tab navigate here (`AppNav`) rather than toggling a hidden boolean. The header
// BurgerMenu stays a local in-place toggle — it is a console control, not a nav
// destination, so it opens the drawer where you are without changing the URL.
export const SETTINGS_ROUTE = '/settings';

function routeWantsSettingsOpen(): boolean {
  return window.location.pathname === SETTINGS_ROUTE;
}

interface SettingsDrawerProviderProps {
  children: ReactNode;
}

export function SettingsDrawerProvider({ children }: SettingsDrawerProviderProps) {
  // Seed from the URL so a direct load / refresh of `/settings` paints the drawer open
  // (every navigation in this app is a full document load, so the provider is fresh per
  // route — reading the URL once at mount is exactly right).
  const [isOpen, setIsOpen] = useState(routeWantsSettingsOpen);
  const [activeSection, setActiveSection] = useState<SettingsSection>('account');

  const open = useCallback((section?: SettingsSection) => {
    if (section) setActiveSection(section);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // On the `/settings` route, closing the drawer means LEAVING the route. Prefer Back
    // (restores the surface you came from — the canvas, a space, the fleet); if `/settings`
    // was a cold entry with nothing behind it in this tab, correct the URL to home. Both
    // keep the browser Back button honest: `/settings` is a real history entry.
    if (window.location.pathname !== SETTINGS_ROUTE) return;
    if (window.history.length > 1) window.history.back();
    else window.history.replaceState(window.history.state, '', '/');
  }, []);

  const value = useMemo(
    () => ({ isOpen, activeSection, open, close, setActiveSection }),
    [isOpen, activeSection, open, close],
  );

  return (
    <SettingsDrawerContext.Provider value={value}>
      {children}
    </SettingsDrawerContext.Provider>
  );
}

export function useSettingsDrawer(): SettingsDrawerState {
  const ctx = useContext(SettingsDrawerContext);
  if (!ctx) {
    throw new Error('useSettingsDrawer must be used inside <SettingsDrawerProvider>');
  }
  return ctx;
}
